import React, { useState, useEffect } from "react";
import { BrowserRouter } from "react-router-dom";
import { StyledEngineProvider } from "@mui/material/styles";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { MsalProvider } from "@azure/msal-react";
import { PublicClientApplication, EventType } from "@azure/msal-browser";
import { CircularProgress, Box } from "@mui/material/";
import Routes from "./Routes";
import Store from "./Store";
import { ThemeSupportProvider } from "./ThemeSupportProvider";
import { msalConfig, loginRequest, b2cPolicies } from "./config/authConfig";
import { setMsalInstance } from "./common/lib/msalTokenHelper";
import config from "./config";

// Initialize MSAL instance for Azure B2C
const msalInstance = new PublicClientApplication(msalConfig);

/**
 * App Component with MSAL initialization
 */
const App = () => {
    const [msalInitialized, setMsalInitialized] = useState(false);

    useEffect(() => {
        const initializeMsal = async () => {
            try {
                // Initialize MSAL
                await msalInstance.initialize();

                // Set MSAL instance reference for token helper
                setMsalInstance(msalInstance);

                // Handle redirect promise - CRITICAL for B2C callback
                const redirectResponse =
                    await msalInstance.handleRedirectPromise();

                // Check redirect response
                if (redirectResponse) {
                    const error = redirectResponse.errorCode;
                    const errorDescription =
                        redirectResponse.errorMessage ||
                        redirectResponse.errorDescription ||
                        "";

                    // Check if this is a password reset request (error AADB2C90118)
                    if (
                        error === "access_denied" &&
                        (errorDescription.includes("AADB2C90118") ||
                            errorDescription.includes("90118") ||
                            redirectResponse.errorCode === "AADB2C90118")
                    ) {
                        console.log(
                            "🔄 Password reset requested (AADB2C90118), redirecting to reset flow..."
                        );

                        // Redirect to password reset flow
                        await msalInstance.loginRedirect({
                            authority: b2cPolicies.resetPassword,
                            scopes: loginRequest.scopes,
                            extraQueryParameters:
                                loginRequest.extraQueryParameters
                        });
                        return; // Don't continue with normal initialization
                    }

                    // Check if we're returning from a successful password reset
                    // Check both authority and ID token claims for the policy name
                    const policyFromAuthority =
                        redirectResponse.authority?.toLowerCase() || "";
                    const policyFromClaims = (
                        redirectResponse.idTokenClaims?.tfp ||
                        redirectResponse.idTokenClaims?.acr ||
                        ""
                    ).toLowerCase();

                    const isPasswordResetFlow =
                        policyFromAuthority.includes("password_reset") ||
                        policyFromClaims.includes("password_reset");

                    if (isPasswordResetFlow && !error) {
                        console.log(
                            "✅ Password reset completed successfully, clearing session and redirecting to login..."
                        );

                        // Clear MSAL cache to prevent auto-login with the reset token
                        await msalInstance.clearCache();

                        // Remove all accounts
                        const accounts = msalInstance.getAllAccounts();
                        accounts.forEach((account) => {
                            try {
                                msalInstance.removeAccount(account);
                            } catch (e) {
                                console.warn("Failed to remove account:", e);
                            }
                        });

                        // Set a flag so the login page can show success message
                        localStorage.setItem("password_reset_success", "true");

                        // Clear URL hash
                        window.history.replaceState(
                            null,
                            "",
                            window.location.pathname + window.location.search
                        );

                        // Hard redirect to login page (don't use loginRedirect, just go to /login)
                        console.log("🔄 Redirecting to /login page");
                        window.location.href = `${window.location.origin}/${config.paths.login}`;
                        return; // Don't continue with normal initialization
                    }
                }

                // Also check URL hash directly as fallback (in case handleRedirectPromise didn't catch it)
                if (window.location.hash) {
                    const hashParams = new URLSearchParams(
                        window.location.hash.substring(1)
                    );
                    const error = hashParams.get("error");
                    const errorDescription =
                        hashParams.get("error_description") || "";

                    if (
                        error === "access_denied" &&
                        (errorDescription.includes("AADB2C90118") ||
                            errorDescription.includes("90118"))
                    ) {
                        console.log(
                            "🔄 Password reset requested from URL hash, redirecting to reset flow..."
                        );

                        // Clear the URL hash
                        window.history.replaceState(
                            null,
                            "",
                            window.location.pathname + window.location.search
                        );

                        // Redirect to password reset flow
                        await msalInstance.loginRedirect({
                            authority: b2cPolicies.resetPassword,
                            scopes: loginRequest.scopes,
                            extraQueryParameters:
                                loginRequest.extraQueryParameters
                        });
                        return; // Don't continue with normal initialization
                    }
                }

                // Set active account if available
                const accounts = msalInstance.getAllAccounts();
                if (accounts.length > 0) {
                    msalInstance.setActiveAccount(accounts[0]);
                }

                // Listen for sign-in events
                msalInstance.addEventCallback((event) => {
                    if (
                        event.eventType === EventType.LOGIN_SUCCESS &&
                        event.payload.account
                    ) {
                        msalInstance.setActiveAccount(event.payload.account);
                    }
                });

                setMsalInitialized(true);
            } catch (error) {
                // Check if error is password reset request (AADB2C90118)
                if (
                    error.errorCode === "AADB2C90118" ||
                    (error.errorMessage &&
                        error.errorMessage.indexOf("AADB2C90118") > -1) ||
                    (error.errorDescription &&
                        error.errorDescription.indexOf("AADB2C90118") > -1)
                ) {
                    console.log(
                        "🔄 Password reset requested from error, redirecting to reset flow..."
                    );
                    try {
                        await msalInstance.loginRedirect({
                            authority: b2cPolicies.resetPassword,
                            scopes: loginRequest.scopes,
                            extraQueryParameters:
                                loginRequest.extraQueryParameters
                        });
                        return; // Don't set initialized, let redirect happen
                    } catch (redirectError) {
                        console.error(
                            "❌ Failed to redirect to password reset:",
                            redirectError
                        );
                    }
                }

                console.error("❌ MSAL initialization failed:", error);
                setMsalInitialized(true); // Still allow app to load
            }
        };

        initializeMsal();
    }, []);

    // Show loading spinner while MSAL initializes
    if (!msalInitialized) {
        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    minHeight: "100vh",
                    backgroundColor: "#f5f5f5"
                }}
            >
                <CircularProgress size={48} />
            </Box>
        );
    }

    return (
        <MsalProvider instance={msalInstance}>
            <LocalizationProvider dateAdapter={AdapterMoment}>
                <StyledEngineProvider injectFirst>
                    <Store>
                        <ThemeSupportProvider>
                            <BrowserRouter
                                future={{
                                    v7_startTransition: true,
                                    v7_relativeSplatPath: true
                                }}
                            >
                                <Routes />
                            </BrowserRouter>
                        </ThemeSupportProvider>
                    </Store>
                </StyledEngineProvider>
            </LocalizationProvider>
        </MsalProvider>
    );
};

export default App;
